import { apiClient } from '@/services/api';

export type ShareRecipientType = 'CONTRACTOR' | 'EXECUTIVE';

export type OpsPlanShareSection =
  | 'schedule' | 'location' | 'venue' | 'securityRequirements'
  | 'medical' | 'evacuationRoute' | 'riskAssessment';

export type OpsPlanSectionsConfig = Partial<Record<OpsPlanShareSection, boolean>>;

export interface OpsPlanShareSummary {
  id: string;
  activityId: string;
  recipientType: ShareRecipientType;
  recipientName: string;
  recipientEmail: string;
  sectionsConfig: OpsPlanSectionsConfig;
  attachmentIds: string[];
  expiresAt: string;
  revokedAt: string | null;
  createdBy: string;
  viewCount: number;
  lastViewedAt: string | null;
  lockedAt: string | null;
  createdAt: string;
  inviteEmailSent?: boolean;
}

export interface CreateSharesPayload {
  recipientType: ShareRecipientType;
  recipients: Array<{ name: string; email: string }>;
  sectionsConfig?: OpsPlanSectionsConfig;
  attachmentIds: string[];
  expiresAt?: string;
}

/** Mirror of the server-side ceilings — UI rendering only; server re-enforces. */
export const SHARE_SECTION_CEILINGS: Record<
  ShareRecipientType,
  Partial<Record<OpsPlanShareSection, { defaultOn: boolean; label: string }>>
> = {
  CONTRACTOR: {
    schedule: { defaultOn: true, label: 'Schedule & overview' },
    location: { defaultOn: true, label: 'Location & map' },
    venue: { defaultOn: true, label: 'Venue & security contact' },
    securityRequirements: { defaultOn: true, label: 'Security requirements' },
    medical: { defaultOn: false, label: 'Hospital & medical info' },
    evacuationRoute: { defaultOn: false, label: 'Evacuation route' },
  },
  EXECUTIVE: {
    schedule: { defaultOn: true, label: 'Schedule & overview' },
    location: { defaultOn: true, label: 'Location & map' },
    venue: { defaultOn: true, label: 'Venue & security contact' },
    medical: { defaultOn: true, label: 'Hospital & medical info' },
    evacuationRoute: { defaultOn: false, label: 'Evacuation route' },
    riskAssessment: { defaultOn: false, label: 'Risk assessment' },
  },
};

const base = (activityId: string) =>
  `/api/v1/executive-protection/activities/${activityId}/shares`;

function unwrap<T>(response: { success: boolean; data?: T; error?: unknown }): T {
  if (!response.success || response.data === undefined) {
    const message = (response.error as { message?: string })?.message ?? 'Request failed';
    throw new Error(message);
  }
  return response.data;
}

export async function listShares(activityId: string): Promise<OpsPlanShareSummary[]> {
  const response = await apiClient.get<{ shares: OpsPlanShareSummary[] }>(base(activityId));
  return unwrap(response).shares;
}

export async function createShares(
  activityId: string,
  payload: CreateSharesPayload
): Promise<OpsPlanShareSummary[]> {
  const response = await apiClient.post<{ shares: OpsPlanShareSummary[] }>(base(activityId), payload);
  return unwrap(response).shares;
}

export async function revokeShare(activityId: string, shareId: string): Promise<void> {
  const response = await apiClient.post<{ revoked: boolean }>(`${base(activityId)}/${shareId}/revoke`, {});
  unwrap(response);
}

export async function resendShare(activityId: string, shareId: string): Promise<void> {
  const response = await apiClient.post<{ resent: boolean }>(`${base(activityId)}/${shareId}/resend`, {});
  unwrap(response);
}
